import React, { useEffect, useMemo, useState } from "react";
import Modal from "../components/ui/Modal";
import TestimonialForm from "../components/testimonials/TestimonialForm";
import { addOrEditTestimonial, approveTestimonial, getAllTestimonials, rejectTestimonial } from "../api/testimonialApi";
import { isApiSuccess, normalizeTestimonials, DEFAULT_TESTIMONIAL_AVATAR } from "../utils/adminData";

const EMPTY_FORM = {
  fullName: "",
  email: "",
  company: "",
  rating: 0,
  message: "",
  photoPreview: "",
};

const AdminTestimonialsPage = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("pending");
  const [feedback, setFeedback] = useState({ type: "", message: "" });
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editingTestimonial, setEditingTestimonial] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);

  const loadTestimonials = async (nextMessage = "") => {
    setLoading(true);

    const response = await getAllTestimonials();
    const normalized = normalizeTestimonials(response);

    setTestimonials(normalized);
    setLoading(false);

    if (response?.Success === false && normalized.length === 0) {
      setFeedback({
        type: "error",
        message: response?.Message || "Unable to load testimonials right now.",
      });
      return;
    }

    if (nextMessage) {
      setFeedback({
        type: "success",
        message: nextMessage,
      });
    }
  };

  useEffect(() => {
    loadTestimonials();
  }, []);

  const testimonialGroups = useMemo(() => ({
    pending: testimonials.filter((item) => item.status === "pending"),
    approved: testimonials.filter((item) => item.status === "approved"),
    rejected: testimonials.filter((item) => item.status === "rejected"),
  }), [testimonials]);

  const activeItems = testimonialGroups[activeTab] ?? [];

  const stats = [
    { id: "pending", label: "Pending", count: testimonialGroups.pending.length },
    { id: "approved", label: "Approved", count: testimonialGroups.approved.length },
    { id: "rejected", label: "Rejected", count: testimonialGroups.rejected.length },
  ];

  const openEditModal = (testimonial) => {
    setEditingTestimonial(testimonial);
    setFormData({
      fullName: testimonial.fullName || "",
      email: testimonial.email || "",
      company: testimonial.company || "",
      rating: testimonial.rating || 0,
      message: testimonial.message || "",
      photoPreview: testimonial.photo || "",
    });
    setIsEditOpen(true);
  };

  const closeEditModal = () => {
    setEditingTestimonial(null);
    setFormData(EMPTY_FORM);
    setIsEditOpen(false);
  };

  const handleFieldChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePhotoChange = (event) => {
    const file = event.target.files && event.target.files[0];

    if (!file) {
      setFormData((prev) => ({ ...prev, photoPreview: "" }));
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setFormData((prev) => ({ ...prev, photoPreview: String(reader.result || "") }));
    };
    reader.readAsDataURL(file);
  };

  const handleStatusUpdate = async (testimonialId, nextStatus) => {
    setFeedback({ type: "", message: "" });

    const response =
      nextStatus === "approved"
        ? await approveTestimonial(testimonialId)
        : await rejectTestimonial(testimonialId);

    if (!isApiSuccess(response)) {
      setFeedback({
        type: "error",
        message: response?.Message || "Unable to update testimonial status.",
      });
      return;
    }

    await loadTestimonials(
      nextStatus === "approved"
        ? "Testimonial approved successfully."
        : "Testimonial rejected successfully."
    );
  };



  const handleEditSubmit = async (event) => {
    event.preventDefault();
    setIsSaving(true);
    setFeedback({ type: "", message: "" });

    const response = await addOrEditTestimonial({
      testimonialId: editingTestimonial?.id,
      id: editingTestimonial?.id,
      fullName: formData.fullName.trim(),
      email: formData.email.trim(),
      company: formData.company.trim(),
      companyName: formData.company.trim(),
      rating: Number(formData.rating) || 0,
      review: formData.message.trim(),
      message: formData.message.trim(),
      imageUrl: formData.photoPreview || DEFAULT_TESTIMONIAL_AVATAR,
      photo: formData.photoPreview || DEFAULT_TESTIMONIAL_AVATAR,
      isApproved: editingTestimonial?.status === "approved",
      status: editingTestimonial?.status,
      approvalStatus: editingTestimonial?.status,
    });

    if (!isApiSuccess(response)) {
      setFeedback({
        type: "error",
        message: response?.Message || "Unable to update testimonial.",
      });
      setIsSaving(false);
      return;
    }

    closeEditModal();
    setIsSaving(false);
    await loadTestimonials("Testimonial updated successfully.");
  };

  return (
    <div className="space-y-4">
      <section className="rounded-md border border-slate-200 bg-white px-4 py-4 sm:px-5">
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1">
            <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Testimonials</h1>
            <p className="text-sm text-slate-500">Manage customer testimonials.</p>
          </div>
          {feedback.message ? (
            <div
              className={`rounded-md px-4 py-3 text-sm ${feedback.type === "error"
                ? "border border-rose-200 bg-rose-50 text-rose-700"
                : "border border-emerald-200 bg-emerald-50 text-emerald-700"
                }`}
            >
              {feedback.message}
            </div>
          ) : null}
        </div>
      </section>

      <section className="flex flex-wrap gap-2">
        {stats.map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => setActiveTab(item.id)}
            className={`rounded-md border px-3 py-2 text-left text-sm transition ${activeTab === item.id
              ? "border-slate-900 bg-slate-900 text-white"
              : "border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50"
              }`}
          >
            <p className={`font-medium ${activeTab === item.id ? "text-white/80" : "text-slate-500"}`}>
              {item.label}
            </p>
            <p className="mt-1 text-lg font-semibold">{item.count}</p>
          </button>
        ))}
      </section>

      <section className="rounded-md border border-slate-200 bg-white px-4 py-4 sm:px-5">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-base font-semibold capitalize text-slate-900">{activeTab} Testimonials</h2>
            <p className="text-sm text-slate-500">{activeItems.length} items in this queue.</p>
          </div>
        </div>

        {loading ? (
          <div className="mt-4 rounded-md border border-slate-200 bg-slate-50 px-4 py-4 text-sm text-slate-500">Loading testimonials...</div>
        ) : activeItems.length ? (
          <div className="mt-4 overflow-x-auto rounded-md border border-slate-200">
            <table className="min-w-full text-left text-sm">
              <thead className="bg-slate-50">
                <tr className="text-slate-500">
                  <th className="px-4 py-3 font-medium">Name</th>
                  <th className="px-4 py-3 font-medium">Company</th>
                  <th className="px-4 py-3 font-medium">Rating</th>
                  <th className="px-4 py-3 font-medium">Message</th>
                  <th className="px-4 py-3 font-medium">Status</th>

                </tr>
              </thead>
              <tbody>
                {activeItems.map((testimonial) => (
                  <tr
                    key={testimonial.id}
                    className="border-t border-slate-200 align-top hover:bg-slate-50"
                  >
                    <td className="px-4 py-3 text-slate-800">
                      <div className="flex items-center gap-3">
                        <img
                          src={testimonial.photo}
                          alt={testimonial.fullName}
                          className="h-10 w-10 rounded-full object-cover"
                        />
                        <div>
                          <p className="font-medium text-slate-900">
                            {testimonial.fullName || "-"}
                          </p>
                          <p className="text-xs text-slate-500">
                            {testimonial.email || "-"}
                          </p>
                        </div>
                      </div>
                    </td>

                    <td className="px-4 py-3 text-slate-800">
                      {testimonial.company || "Individual Client"}
                    </td>

                    <td className="px-4 py-3 text-slate-800">
                      {testimonial.rating || 0}
                    </td>

                    <td className="max-w-sm px-4 py-3 text-slate-800">
                      {testimonial.message || "-"}
                    </td>

                    <td className="px-4 py-3 capitalize text-slate-800">
                      {testimonial.status}
                    </td>

                    <td className="px-4 py-3">
                      {testimonial.status === "pending" ? (
                        <div className="flex flex-wrap gap-2">
                          <button
                            type="button"
                            onClick={() => handleStatusUpdate(testimonial.id, "approved")}
                            className="rounded-md border border-emerald-600 bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white"
                          >
                            Approve
                          </button>

                          <button
                            type="button"
                            onClick={() => openEditModal(testimonial)}
                            className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700"
                          >
                            Edit
                          </button>

                          <button
                            type="button"
                            onClick={() => handleStatusUpdate(testimonial.id, "rejected")}
                            className="rounded-md border border-rose-600 bg-rose-600 px-3 py-1.5 text-xs font-medium text-white"
                          >
                            Reject
                          </button>
                        </div>
                      ) : (
                        <></>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="mt-4 rounded-md border border-slate-200 bg-slate-50 px-4 py-4 text-sm text-slate-500">No testimonials in this state.</div>
        )}
      </section>

      <Modal isOpen={isEditOpen} onClose={closeEditModal} title="Edit Testimonial" maxWidth="max-w-3xl">
        <TestimonialForm
          formData={formData}
          onFieldChange={handleFieldChange}
          onPhotoChange={handlePhotoChange}
          onSubmit={handleEditSubmit}
          submitLabel="Save Changes"
          isSubmitting={isSaving}
          submitDisabled={!formData.rating}
        />
      </Modal>
    </div>
  );
};

export default AdminTestimonialsPage;
